import PageHeader from "@/components/PageHeader";
import Icon from "@/components/Icon";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Check } from "lucide-react";
import { useQuote } from "@/components/QuoteDialog";
import { services, industries, socFeatures, images, complianceSteps, complianceFrameworks, serviceDetails } from "@/mock/mock";

export default function Services() {
  const { openQuote } = useQuote();
  return (
    <>
      <PageHeader
        eyebrow="Services"
        title="Managed cyber security services that protect what matters"
        subtitle="Our services cover every major segment of data protection and information security — delivered as a managed service from our SOC in Halifax, Nova Scotia."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 70}>
              <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-7 card-hover">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-lime/10 text-brand-lime">
                  <Icon name={s.icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.short}</p>
                <span className="mt-5 inline-block rounded-full border border-white/10 px-3 py-1 text-[11px] font-medium text-slate-400">{s.tag}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-20 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Specialized Practices</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">Deep-dive capabilities</h2>
            <p className="mt-4 text-slate-400">Dedicated practice areas delivering defensible, measurable outcomes for your security program.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {serviceDetails.map((d, i) => (
              <Reveal key={d.title} delay={(i % 3) * 90}>
                <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-8 card-hover">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-lime text-brand-navy">
                    <Icon name={d.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-bold text-white">{d.title}</h3>
                  <p className="mt-2 text-sm text-slate-400">{d.intro}</p>
                  <ul className="mt-5 space-y-2.5">
                    {d.items.map((it) => (
                      <li key={it} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="h-4 w-4 shrink-0 text-brand-lime mt-0.5" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      <section className="py-20 border-y border-white/10 bg-brand-navy2/40">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10 glow-lime">
              <img src={images.dataCenter} alt="Data center infrastructure" className="w-full h-[400px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Why an MSSP</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">One partner for detection, response and resilience</h2>
            <p className="mt-4 text-slate-400">We combine people, process and technology so you get enterprise-grade security without building it all in-house.</p>
            <ul className="mt-7 space-y-4">
              {socFeatures.map((f) => (
                <li key={f} className="flex items-start gap-3 text-slate-300">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-brand-lime mt-0.5" /><span>{f}</span>
                </li>
              ))}
            </ul>
            <Button onClick={openQuote} className="group mt-8 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full px-7 h-12">
              Request a proposal<ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Industries</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">Sectors we secure</h2>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {industries.map((ind) => (
              <span key={ind} className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-slate-300 hover:border-brand-lime/40 hover:text-brand-lime transition-colors">{ind}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Compliance Implementation</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">From gap analysis to audit-ready — end to end</h2>
            <p className="mt-4 text-slate-400">We implement the controls, evidence and processes behind the certifications your customers and regulators expect — so compliance becomes a byproduct of good security, not a paperwork marathon.</p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {complianceSteps.map((step, i) => (
              <Reveal key={step.title} delay={(i % 4) * 70}>
                <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-7 card-hover">
                  <span className="font-display text-sm font-bold text-brand-navy bg-brand-lime rounded-full h-8 w-8 grid place-items-center">{i + 1}</span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-white">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            {complianceFrameworks.map((f) => (
              <span key={f} className="rounded-full border border-brand-lime/30 bg-brand-lime/5 px-5 py-2.5 text-sm font-medium text-brand-lime">{f}</span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
